import axiosClient from '@/api/axiosClient'
import {
  IIncomeDetails,
  IManualIncome,
  IncomeFrequency,
  IPrimaryIncome,
  IRecurringIncome,
  ManualIncomeStatus,
} from '@/types'

// The gateway emits every field (EmitUnpopulated), writes int64s as strings and leaves unset
// timestamps as null, so the raw reply is loosely typed and normalised before the UI sees it.
type Int64 = string | number | null | undefined

interface RawPrimaryIncome {
  id: Int64
  amount: number
  payType: string
  frequency: string
  payDayOfWeek: Int64
  payDayOfMonth: Int64
  startDate: string | null
  suggestedStartDate: string | null
  payCycleScore: number
  description: string
  createdAt: string | null
  updatedAt: string | null
}

interface RawRecurringIncome {
  id: Int64
  amount: number
  payType: string
  description?: string | null
  recurringFrequency: string
  payDayOfWeek?: string | null
  payDayOfMonth?: Int64
  startDate: string | null
  endDate: string | null
  suggestedStartDate: string | null
  basiqFinancialAccountId?: string | null
  expectedRecurringCreditCount?: Int64
  recurringCreditCount?: Int64
  payCycleScore: number
  isPrimaryIncome: boolean
  modelVersion: string
  createdAt: string | null
}

interface RawIncomeDetailsResponse {
  data?: {
    primaryIncome?: RawPrimaryIncome | null
    recurringIncomes?: RawRecurringIncome[] | null
  } | null
}

const toNumber = (value: Int64): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// Primary income always carries both pay day fields, defaulting to 0. Day 0 of the month is not a
// day, so a monthly income is recognised by a positive day of month, and only then is the weekday's
// 0 read as "unset" rather than Sunday.
const toPrimaryIncome = (raw: RawPrimaryIncome): IPrimaryIncome => {
  const dayOfMonth = toNumber(raw.payDayOfMonth)
  const isMonthly = (dayOfMonth ?? 0) > 0 || raw.frequency === 'MONTHLY'
  return {
    id: toNumber(raw.id) ?? 0,
    amount: raw.amount,
    payType: raw.payType,
    recurringFrequency: raw.frequency,
    payDayOfWeek: isMonthly ? null : toNumber(raw.payDayOfWeek),
    payDayOfMonth: dayOfMonth && dayOfMonth > 0 ? dayOfMonth : null,
    startDate: raw.startDate,
    suggestedStartDate: raw.suggestedStartDate,
    payCycleScore: raw.payCycleScore,
    description: raw.description,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

const toRecurringIncome = (raw: RawRecurringIncome): IRecurringIncome => ({
  id: toNumber(raw.id) ?? 0,
  amount: raw.amount,
  payType: raw.payType,
  description: raw.description,
  recurringFrequency: raw.recurringFrequency,
  payDayOfWeek: raw.payDayOfWeek || null,
  payDayOfMonth: toNumber(raw.payDayOfMonth),
  startDate: raw.startDate,
  endDate: raw.endDate,
  suggestedStartDate: raw.suggestedStartDate,
  basiqFinancialAccountId: raw.basiqFinancialAccountId,
  expectedRecurringCreditCount: toNumber(raw.expectedRecurringCreditCount),
  recurringCreditCount: toNumber(raw.recurringCreditCount),
  payCycleScore: raw.payCycleScore,
  isPrimaryIncome: raw.isPrimaryIncome,
  modelVersion: raw.modelVersion,
  createdAt: raw.createdAt,
})

// The API answers 404 with this code when the customer has neither a primary nor a recurring
// income. That is an empty result, not a failure.
const INCOME_DETAILS_NOT_FOUND = 'USER_INCOME_DETAILS_NOT_FOUND'

const isIncomeNotFound = (error: unknown) => {
  const err = error as { response?: { status?: number; data?: { error?: { code?: string }[] } } }
  return (
    err?.response?.status === 404 &&
    !!err.response.data?.error?.some(e => e?.code === INCOME_DETAILS_NOT_FOUND)
  )
}

const EMPTY_INCOME_DETAILS: IIncomeDetails = { primaryIncome: null, recurringIncomes: [] }

// support.income.v1.ManualIncome as the gateway serialises it. status (ManualIncomeStatus) and
// recurring_frequency (shared.Frequency) are enums the gateway writes without their prefix, e.g.
// PENDING and FORTNIGHTLY; protojson leaves out a zero (UNSPECIFIED) enum. last_paid_amount is
// int64 cents, which protojson writes as a string. Every response wraps it as data.income.
interface ManualIncomeWire {
  id: string
  status?: string
  bankAccountId: string
  lastPaidAmount: string | number
  lastPayDay: string
  recurringFrequency?: string
  createdAt: string
  updatedAt?: string
}

const MANUAL_INCOME_STATUSES: ManualIncomeStatus[] = ['PENDING', 'SUCCESS', 'REJECTED']
const INCOME_FREQUENCIES: IncomeFrequency[] = ['WEEKLY', 'FORTNIGHTLY', 'MONTHLY']

// A value the panel does not know yet (a status added later) reads as UNSPECIFIED, not as-is.
const oneOf = <T extends string>(allowed: T[], value: string | undefined, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback

export const toManualIncome = (wire: ManualIncomeWire): IManualIncome => ({
  id: wire.id,
  status: oneOf(MANUAL_INCOME_STATUSES, wire.status, 'UNSPECIFIED'),
  bankAccountId: wire.bankAccountId,
  lastPaidAmount: Number(wire.lastPaidAmount),
  lastPayDay: wire.lastPayDay,
  recurringFrequency: oneOf(INCOME_FREQUENCIES, wire.recurringFrequency, 'UNSPECIFIED'),
  createdAt: wire.createdAt,
  updatedAt: wire.updatedAt,
})

// The customer is passed in rather than read from IndexedDB: that record is shared by every tab,
// so picking another customer in a second tab would otherwise retarget a write confirmed here.
// VITE_API_BASE_URL already ends in /support, so the full paths are /support/users/{user_id}/....
export const IncomeService = {
  // support.income.v1.IncomeService/TriggerIncomeDetection. Starts FHS income detection for one
  // customer and replies with an empty body: the detection itself runs asynchronously, so the reply
  // only confirms it was started.
  triggerIncomeDetection: async (userId: string): Promise<void> => {
    await axiosClient.post(`/users/${encodeURIComponent(userId)}/income-detection`)
  },

  // support.income.v1.IncomeService/GetUserManualIncome. The gateway answers 404 when the customer
  // has not submitted a manual income, which is an expected state rather than a failure, so it
  // resolves to null.
  fetchManualIncome: async (userId: string): Promise<IManualIncome | null> => {
    try {
      const response = await axiosClient.get(`/users/${encodeURIComponent(userId)}/manual-income`)
      const income = response.data.data?.income
      return income ? toManualIncome(income) : null
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) return null
      throw error
    }
  },

  // support.income.v1.IncomeService/ApproveUserManualIncome and RejectUserManualIncome (AIP-136
  // custom methods). Each acts on the customer's pending manual income, so no request id is sent;
  // the body is bound with "*" and carries nothing beyond the path. The reply is the request as
  // stored after the review.
  approveManualIncome: async (userId: string): Promise<IManualIncome> => {
    const response = await axiosClient.post(
      `/users/${encodeURIComponent(userId)}/manual-income:approve`,
      {}
    )
    return toManualIncome(response.data.data.income)
  },

  rejectManualIncome: async (userId: string): Promise<IManualIncome> => {
    const response = await axiosClient.post(
      `/users/${encodeURIComponent(userId)}/manual-income:reject`,
      {}
    )
    return toManualIncome(response.data.data.income)
  },

  // support.income.v1.IncomeService/GetUserIncomeDetails: the primary income and every recurring
  // income for one customer in a single read. Like detection, the customer is passed in.
  getIncomeDetails: async (userId: string): Promise<IIncomeDetails> => {
    try {
      const { data } = await axiosClient.get<RawIncomeDetailsResponse>(
        `/users/${encodeURIComponent(userId)}/income-details`
      )
      const primary = data?.data?.primaryIncome
      return {
        primaryIncome: primary ? toPrimaryIncome(primary) : null,
        recurringIncomes: (data?.data?.recurringIncomes ?? []).map(toRecurringIncome),
      }
    } catch (error) {
      if (isIncomeNotFound(error)) return EMPTY_INCOME_DETAILS
      throw error
    }
  },
}
